import { css } from '@emotion/core';
import { N200A, N900 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import {
  alignItemsStart,
  flexDirectionColumn,
  flexStart,
  spaceGapCozy,
  spaceTopCompact,
  spaceTopCozy,
  SPACE_COZY,
  SEARCH_SMALL_BREAKPOINT,
  flex,
  SPACE_COMFORTABLE,
  SPACE_COMPACT,
  spaceTopMinimum,
  flexSpace,
  spaceTopComfortable,
  flexCenter,
  spaceTopRelaxed,
  flexEnd,
  SPACE_MINIMUM,
} from '@/styles';

const sidebarWidth = 200;
const sidebarStackBreakpoint = SEARCH_SMALL_BREAKPOINT;

export const searchFieldStyles = [
  spaceTopCompact,
  // overwrite the default atlaskit text field
  css`
    &.search-view-text-field {
      border-radius: 5px;
      background: none;
      padding-left: 10px;
    }
  `,
];

export const searchStatusBarStyles = css`
  margin-top: 0;
`;

export const searchFieldStatusStyles = css`
  margin-right: 8px;
`;

export const searchActionBarStyles = css`
  ${spaceTopCozy}
  ${flexCenter}
  height: 32px; /* avoid flashing after pagination is loaded */
`;
export const searchMainStyles = [flexStart, spaceTopComfortable, alignItemsStart, spaceGapCozy];
export const searchRefinerStyles = [
  css`
    box-sizing: border-box;
    width: ${sidebarWidth}px;
    h4 {
      font-weight: 400;
      font-size: 20px;
      line-height: 1.5;
      margin-top: 10px;
      padding-bottom: ${SPACE_COZY}px;
    }
    section {
      ${spaceTopCozy}
      padding-top: ${SPACE_COZY}px;
      border-top: 1px solid ${token('color.background.neutral.subtle.hovered', N200A)};
      ul {
        padding-left: 0;
        li {
          list-style: none;
        }
      }
      & > ul > li {
        margin-top: 12px;
        li {
          margin: 8px 0 0 21px;
          font-size: 13px;
        }
      }
      &:first-of-type {
        margin-top: 0;
        border-top: none;
        padding-top: 0;
      }
    }
  `,
];

export const checkboxLiStyles = css`
  position: relative;
  left: -5px;
`;

export const searchContentStyles = [
  flexDirectionColumn,
  css`
    flex-grow: 1;
    width: calc(100% - ${sidebarWidth}px - ${SPACE_COZY}px);
  `,
];

export const searchResultStyles = [spaceTopRelaxed];

const searchResultTypeWidth = 150;
const searchStatusWidth = 210;
const searchInfoWidth = 185;
const searchTeamCardTypeWidth = 28;
const searchTeamCardStatusWidth = 400;
const searchTeamCardInfoWidth = 169;

// Comfort view
const searchResultTypeComfortWidth = 140;
const searchTeamCardInfoComfortWidth = 140;

const capabilityResultItemWidth = 110;
const capabilityProductItemWidth = capabilityResultItemWidth * 3;
export const capabilityResultRowStyles = [
  flexStart,
  css`
    a span {
      color: ${token('color.text', N900)};
      &:hover {
        text-decoration: underline;
        text-decoration-color: ${token('color.text', N900)};
      }
    }
    & > div {
      word-break: break-word;
      ${flexCenter};
      width: ${capabilityResultItemWidth}px;

      &:first-of-type {
        width: ${searchTeamCardTypeWidth}px;
      }
      &:nth-of-type(2) {
        ${flexStart};
        width: calc(
          100% -
            ${searchTeamCardTypeWidth +
              capabilityProductItemWidth +
              capabilityResultItemWidth * 3 +
              capabilityResultItemWidth * 0.6}px
        );
      }
      &:nth-of-type(3) {
        ${flexStart};
        width: ${capabilityProductItemWidth}px;
      }
      &:last-of-type {
        width: ${capabilityResultItemWidth * 0.6}px;
        ${flexEnd};
        & > span {
          margin-right: ${SPACE_MINIMUM}px;
        }
      }
    }
  `,
];

export const capabilitySearchResultRowStyles = css`
  & > div {
    &:nth-of-type(2) {
      width: calc(
        100% -
          ${searchTeamCardTypeWidth +
            capabilityResultItemWidth * 3 +
            capabilityResultItemWidth * 0.6}px
      );
    }
    &:nth-of-type(3) {
      ${flexCenter};
      width: ${capabilityResultItemWidth}px;
    }
  }
`;

export const searchResultRowStyles = [
  flexSpace,
  css`
    & > div {
      &:first-of-type {
        width: ${searchResultTypeWidth}px;
        span {
          cursor: pointer;
        }
      }
      &:nth-of-type(2) {
        width: calc(100% - ${searchResultTypeWidth + searchInfoWidth + searchStatusWidth}px);
      }
      &:nth-of-type(3) {
        width: ${searchStatusWidth}px;
      }
      &:nth-of-type(4) {
        width: ${searchInfoWidth}px;
      }
    }
  `,
];

export const searchResultRowComfortStyles = css`
  & > div {
    &:first-of-type {
      width: ${searchResultTypeComfortWidth}px;
    }
    &:nth-of-type(2) {
      width: calc(100% - ${searchResultTypeComfortWidth + searchStatusWidth}px);
    }
  }
`;

export const searchResultCardRowStyles = [
  flexSpace,
  css`
    & > div {
      &:first-of-type {
        width: ${searchTeamCardTypeWidth}px;
      }
      &:nth-of-type(2) {
        width: calc(
          100% - ${searchTeamCardTypeWidth + searchTeamCardStatusWidth + searchTeamCardInfoWidth}px
        );
      }
      &:nth-of-type(3) {
        width: ${searchTeamCardStatusWidth}px;
      }
      &:nth-of-type(4) {
        width: ${searchTeamCardInfoWidth}px;
      }
    }
  `,
];

export const searchResultCardRowComfortStyles = [
  flexSpace,
  css`
    & > div {
      &:nth-of-type(4) {
        width: ${searchTeamCardInfoComfortWidth}px;
      }
    }
  `,
];

// Media query adjustments for small screen width
searchMainStyles.push(css`
  @media screen and (max-width: ${sidebarStackBreakpoint}px) {
    ${spaceTopCompact}
  }
`);
searchRefinerStyles.push(css`
  @media screen and (max-width: ${sidebarStackBreakpoint}px) {
    width: 100%;
    ${flex}
    flex-wrap: wrap;
    gap: ${SPACE_COMPACT}px ${SPACE_COMFORTABLE * 2}px;
    section {
      border-top: none;
      margin-top: 0;
      padding-top: 0;
      ${flexStart}
      &:first-of-type {
        width: 100%;
      }
      h6 {
        margin-right: ${SPACE_COMFORTABLE}px;
      }
      ul {
        margin-top: 0;
        display: flex;
        li {
          margin-top: 0;
        }
      }
    }
  }
`);

searchContentStyles.push(css`
  @media screen and (max-width: ${sidebarStackBreakpoint}px) {
    width: 100%;
    ${spaceTopMinimum}
  }
`);
